import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { it } from "date-fns/locale";

import { api } from "../lib/api";
import { TopBar } from "../components/ui/TopBar";
import { Icon } from "../components/ui/Icon";
import { Modal } from "../components/ui/Modal";
import { EventBadge } from "../components/ui/EventBadge";
import { SectionTitle } from "../components/ui/SectionTitle";
import { Button } from "../components/ui/Button";
import { ProductThumb } from "../components/ui/ProductThumb";

type EventRow = {
  id: number;
  event_type: string;
  sku: string;
  name: string | null;
  image_url: string | null;
  quantity_delta: number;
  operator: string | null;
  reason: string | null;
  notes: string | null;
  related_transfer_id: number | null;
  related_order_id: string | null;
  source_platform: string;
  batch_id: string | null;
  batch_label: string | null;
  propagated_to_shopify: boolean;
  propagated_to_odoo: boolean;
  error_log: string | null;
  created_at: string;
};

const EVENT_TYPES = [
  { value: "", label: "Tutti i tipi" },
  { value: "stock_in", label: "Stock IN" },
  { value: "stock_out", label: "Stock OUT" },
  { value: "transfer_out", label: "Transfer OUT" },
  { value: "transfer_in", label: "Transfer IN" },
  { value: "inventory_adjust", label: "Inventario" },
  { value: "tiktok_sale", label: "Vendite TikTok" },
  { value: "shopify_sale", label: "Vendite Shopify" },
  { value: "reconcile_check", label: "Sync check" },
];

function midnightIso(daysAgo: number = 0): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString();
}

export function Events() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<EventRow | null>(null);
  const [page, setPage] = useState(1);

  const fromParam = params.get("from") || "";
  const typeParam = params.get("type") || "";
  const skuParam = params.get("sku") || "";
  const operatorParam = params.get("operator") || "";

  const [sku, setSku] = useState(skuParam);
  const [operator, setOperator] = useState(operatorParam);

  const dateFrom = useMemo(() => {
    if (fromParam === "today") return midnightIso(0);
    if (fromParam === "7d") return midnightIso(7);
    if (fromParam === "30d") return midnightIso(30);
    return undefined;
  }, [fromParam]);

  const { data: events, isFetching } = useQuery<EventRow[]>({
    queryKey: ["events", "list", fromParam, typeParam, sku, operator, page],
    queryFn: async () =>
      (
        await api.get("/api/events", {
          params: {
            date_from: dateFrom,
            event_type: typeParam || undefined,
            sku: sku || undefined,
            operator: operator || undefined,
            page,
            page_size: 50,
          },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  }

  function applyFreeFilters(e: React.FormEvent) {
    e.preventDefault();
    setFilter("sku", sku.trim());
    setFilter("operator", operator.trim());
  }

  const rows = events ?? [];

  return (
    <>
      <TopBar back onBack={() => nav("/")} title="Eventi storico" sub="Audit trail completo" />

      <div style={{ padding: "14px 18px 0" }}>
        <div className="tcg-card" style={{ padding: 12 }}>
          <form onSubmit={applyFreeFilters} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                className="tcg-select"
                value={typeParam}
                onChange={(e) => setFilter("type", e.target.value)}
                style={{ flex: 1 }}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <select
                className="tcg-select"
                value={fromParam}
                onChange={(e) => setFilter("from", e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Sempre</option>
                <option value="today">Oggi</option>
                <option value="7d">Ultimi 7 giorni</option>
                <option value="30d">Ultimi 30 giorni</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="tcg-input tcg-mono"
                placeholder="SKU…"
                value={sku}
                onChange={(e) => setSku(e.target.value.toUpperCase().trim())}
                style={{ flex: 1 }}
              />
              <input
                className="tcg-input"
                placeholder="Operatore…"
                value={operator}
                onChange={(e) => setOperator(e.target.value.toLowerCase().trim())}
                style={{ flex: 1 }}
              />
              <button
                type="submit"
                className="tcg-btn tcg-btn-primary"
                style={{ padding: "10px 14px" }}
              >
                <Icon name="search" size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>

      <SectionTitle
        right={
          <span style={{ paddingRight: 18, fontSize: 11, color: "var(--tcg-muted)", fontWeight: 700 }}>
            {isFetching ? "···" : `${rows.length} eventi · pag. ${page}`}
          </span>
        }
      >
        Risultati
      </SectionTitle>

      <div
        style={{
          background: "var(--tcg-card)",
          marginInline: 18,
          borderRadius: 16,
          border: "1px solid var(--tcg-border)",
          overflow: "hidden",
        }}
      >
        {rows.length === 0 && (
          <div style={{ padding: 18, textAlign: "center", color: "var(--tcg-muted)", fontSize: 12 }}>
            Nessun evento per i filtri scelti.
          </div>
        )}
        {rows.map((e, i, arr) => {
          const delta = e.quantity_delta;
          const qty = delta === 0 ? "0" : `${delta > 0 ? "+" : "−"}${Math.abs(delta)}`;
          const qtyColor =
            delta > 0
              ? "var(--tcg-success)"
              : delta < 0
                ? "var(--tcg-danger)"
                : "var(--tcg-ink-soft)";
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => setSelected(e)}
              style={{
                display: "flex",
                gap: 10,
                padding: "12px 12px",
                borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--tcg-border)",
                alignItems: "flex-start",
                background: "transparent",
                border: "none",
                width: "100%",
                cursor: "pointer",
                fontFamily: "inherit",
                color: "inherit",
                textAlign: "left",
              }}
            >
              <div
                className="tcg-mono"
                style={{
                  fontSize: 10,
                  color: "var(--tcg-muted)",
                  fontWeight: 700,
                  width: 56,
                  paddingTop: 3,
                  flexShrink: 0,
                }}
              >
                {format(new Date(e.created_at), "dd/MM HH:mm")}
              </div>
              <EventBadge type={e.event_type} />
              <ProductThumb imageUrl={e.image_url} sku={e.sku} size="xs" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: "var(--tcg-ink)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {e.name || e.sku}
                </div>
                <div
                  className="tcg-mono"
                  style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 2, fontWeight: 600 }}
                >
                  {e.sku} · {e.reason || labelForEvent(e.event_type)} · {e.operator || "sistema"}
                </div>
                {e.batch_label && (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "var(--tcg-info)",
                      marginTop: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    ⬡ {e.batch_label}
                  </div>
                )}
              </div>
              <div
                className="tcg-num"
                style={{ fontSize: 13, fontWeight: 800, color: qtyColor, flexShrink: 0, paddingTop: 1 }}
              >
                {qty}
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ padding: "14px 18px 0", display: "flex", gap: 8, alignItems: "center" }}>
        <Button
          variant="secondary"
          disabled={page <= 1 || isFetching}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          <Icon name="chev-l" /> Precedente
        </Button>
        <Button
          variant="secondary"
          disabled={rows.length < 50 || isFetching}
          onClick={() => setPage((p) => p + 1)}
        >
          Successivo <Icon name="chev-r" />
        </Button>
      </div>

      <div style={{ height: 24 }} />

      {selected && (
        <Modal
          open
          onClose={() => setSelected(null)}
          title={`Evento #${selected.id}`}
          tone={selected.quantity_delta < 0 ? "danger" : selected.quantity_delta > 0 ? "success" : "default"}
          footer={
            <Button variant="secondary" block onClick={() => setSelected(null)}>
              Chiudi
            </Button>
          }
        >
          <EventDetail e={selected} />
        </Modal>
      )}
    </>
  );
}

function EventDetail({ e }: { e: EventRow }) {
  const rows: Array<[string, React.ReactNode]> = [
    ["Tipo", <EventBadge type={e.event_type} key="t" />],
    [
      "Prodotto",
      <div key="p" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <ProductThumb imageUrl={e.image_url} sku={e.sku} size="sm" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>{e.name || "—"}</div>
          <div className="tcg-mono" style={{ fontSize: 11, color: "var(--tcg-muted)" }}>
            {e.sku}
          </div>
        </div>
      </div>,
    ],
    [
      "Quantità",
      <span
        key="q"
        className="tcg-num"
        style={{
          color: e.quantity_delta > 0 ? "var(--tcg-success)" : e.quantity_delta < 0 ? "var(--tcg-danger)" : "var(--tcg-ink)",
          fontWeight: 800,
        }}
      >
        {e.quantity_delta > 0 ? "+" : ""}
        {e.quantity_delta}
      </span>,
    ],
    ["Operatore", e.operator || "sistema"],
    ["Data", format(new Date(e.created_at), "EEEE d MMMM yyyy 'alle' HH:mm:ss", { locale: it })],
    ["Sorgente", <span className="tcg-mono" key="src">{e.source_platform}</span>],
  ];
  if (e.reason) rows.push(["Causale", e.reason]);
  if (e.batch_label) rows.push(["Batch produzione", e.batch_label]);
  if (e.notes) rows.push(["Note", e.notes]);
  if (e.related_transfer_id) rows.push(["Transfer", `#${e.related_transfer_id}`]);
  if (e.related_order_id) rows.push(["Ordine", <span className="tcg-mono" key="o">{e.related_order_id}</span>]);
  if (e.error_log)
    rows.push([
      "Errori propagazione",
      <pre
        key="err"
        style={{
          margin: 0,
          fontSize: 11,
          background: "var(--tcg-danger-bg)",
          color: "var(--tcg-danger)",
          padding: 8,
          borderRadius: 8,
          whiteSpace: "pre-wrap",
        }}
      >
        {e.error_log}
      </pre>,
    ]);

  return (
    <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 12px" }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: "var(--tcg-muted)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              alignSelf: "start",
              paddingTop: 2,
            }}
          >
            {k}
          </dt>
          <dd style={{ margin: 0, fontSize: 13, color: "var(--tcg-ink)", fontWeight: 600 }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function labelForEvent(t: string): string {
  switch (t) {
    case "stock_in":
      return "Stock in entrata";
    case "stock_out":
      return "Uscita straordinaria";
    case "transfer_out":
      return "Carico viaggio";
    case "transfer_in":
      return "Rientro viaggio";
    case "inventory_adjust":
      return "Rettifica inventario";
    case "tiktok_sale":
      return "Vendita TikTok Shop";
    case "shopify_sale":
      return "Vendita Shopify";
    case "reconcile_check":
      return "Sync piattaforme";
    default:
      return t;
  }
}
